import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Fingerprint, 
  Search, 
  MapPin, 
  Phone, 
  Mail, 
  Globe,
  Clock,
  CheckCircle,
  Star,
  Filter,
  ChevronDown,
  Building2,
  Navigation
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FindProviderPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [providers, setProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch providers from API
  useEffect(() => {
    fetchProviders();
  }, []);

  useEffect(() => {
    filterProviders();
  }, [providers, searchQuery, serviceFilter]);

  const fetchProviders = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/providers`);
      if (response.ok) {
        const data = await response.json();
        setProviders(data.providers || []);
      } else {
        // Use demo data if API not available
        setProviders(demoProviders);
      }
    } catch (err) {
      console.error('Failed to fetch providers:', err);
      // Use demo data as fallback
      setProviders(demoProviders);
    } finally {
      setLoading(false);
    }
  };

  const filterProviders = () => {
    let filtered = [...providers];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(query) ||
        p.city.toLowerCase().includes(query) ||
        p.state.toLowerCase().includes(query) ||
        p.zip.includes(query)
      );
    }
    
    if (serviceFilter !== 'all') {
      filtered = filtered.filter(p => p.services.includes(serviceFilter));
    }
    
    setFilteredProviders(filtered);
  };

  // Demo providers data (shown when API not available)
  const demoProviders = [
    {
      id: '1',
      name: 'Trusted Independent Networking Group',
      address: '123 Main Street, Suite 200',
      city: 'Yonkers',
      state: 'NY',
      zip: '10701',
      phone: '(914) 555-0100',
      email: 'info@tingfingerprints.com',
      website: 'https://tingfingerprints.com',
      hours: 'Mon-Fri: 9AM-5PM, Sat: 10AM-2PM',
      services: ['employment', 'licensing', 'immigration', 'volunteer'],
      rating: 4.9,
      reviews: 127,
      verified: true
    },
    {
      id: '2',
      name: 'Metro Fingerprinting Services',
      address: '456 Broadway',
      city: 'New York',
      state: 'NY',
      zip: '10013',
      phone: '(212) 555-0200',
      email: 'contact@metrofp.com',
      website: 'https://metrofp.com',
      hours: 'Mon-Fri: 8AM-6PM',
      services: ['employment', 'licensing', 'security'],
      rating: 4.7,
      reviews: 89,
      verified: true
    },
    {
      id: '3',
      name: 'Quick Print ID Services',
      address: '789 Park Avenue',
      city: 'White Plains',
      state: 'NY',
      zip: '10601',
      phone: '(914) 555-0300',
      email: 'hello@quickprintid.com',
      website: null,
      hours: 'Mon-Sat: 9AM-5PM',
      services: ['employment', 'volunteer', 'immigration'],
      rating: 4.5,
      reviews: 45,
      verified: true
    },
    {
      id: '4',
      name: 'Garden State Fingerprinting',
      address: '321 Central Avenue',
      city: 'Newark',
      state: 'NJ',
      zip: '07102',
      phone: '(973) 555-0400',
      email: 'info@gsfp.com',
      website: 'https://gardenstateprints.com',
      hours: 'Mon-Fri: 9AM-6PM, Sat: By Appointment',
      services: ['employment', 'licensing', 'security', 'legal'],
      rating: 4.8,
      reviews: 72,
      verified: true
    },
    {
      id: '5',
      name: 'Connecticut Background Services',
      address: '555 Post Road',
      city: 'Stamford',
      state: 'CT',
      zip: '06901',
      phone: '(203) 555-0500',
      email: 'support@ctbackground.com',
      website: 'https://ctbackground.com',
      hours: 'Mon-Fri: 8:30AM-5:30PM',
      services: ['employment', 'licensing', 'volunteer', 'immigration'],
      rating: 4.6,
      reviews: 58,
      verified: true
    }
  ];

  const serviceOptions = [
    { value: 'all', label: 'All Services' },
    { value: 'employment', label: 'Employment Background Checks' },
    { value: 'licensing', label: 'Professional Licensing' },
    { value: 'immigration', label: 'Immigration & Visas' },
    { value: 'security', label: 'Security Clearances' },
    { value: 'volunteer', label: 'Volunteer & Foster Care' },
    { value: 'legal', label: 'Legal & Court Orders' }
  ];

  const getServiceLabel = (service) => {
    const option = serviceOptions.find(o => o.value === service);
    return option ? option.label : service;
  };

  return (
    <div className="min-h-screen bg-slate-900 relative" data-testid="find-provider-page">
      {/* Background */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/07e51985-c374-4472-a4ec-7832a182bb43/images/a599bfb20d8b730453411fb0496e41df29ac3936a599b1146f16c28f94161081.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      />
      <div className="fixed inset-0 z-0 bg-slate-900/90" />
      
      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <header className="border-b border-white/10 bg-slate-900/50 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Fingerprint className="w-8 h-8 text-blue-400" />
              <span className="text-xl font-bold text-white">FingerprintVS</span>
            </Link>
            <nav className="flex items-center gap-6">
              <Link to="/services" className="text-gray-300 hover:text-white transition">Services</Link>
              <Link to="/find-provider" className="text-blue-400 font-medium">Find a Provider</Link>
              <Link to="/login" className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition">
                Provider Login
              </Link>
            </nav>
          </div>
        </header>

        {/* Search Section */}
        <section className="py-12 px-4">
          <div className="max-w-4xl mx-auto text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Find a Fingerprinting Provider
            </h1>
            <p className="text-gray-400">
              Search for certified providers in your area by location or service type.
            </p>
          </div>

          {/* Search Controls */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Location Search */}
                <div className="flex-1 relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="City, State, or ZIP Code"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition"
                    data-testid="location-search"
                  />
                </div>
                
                {/* Service Filter */}
                <div className="md:w-64 relative">
                  <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={serviceFilter}
                    onChange={(e) => setServiceFilter(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white appearance-none focus:outline-none focus:border-blue-500 transition cursor-pointer"
                    data-testid="service-filter"
                  >
                    {serviceOptions.map(option => (
                      <option key={option.value} value={option.value} className="bg-slate-800">
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="pb-16 px-4">
          <div className="max-w-4xl mx-auto">
            <p className="text-gray-400 mb-6">
              {filteredProviders.length} provider{filteredProviders.length !== 1 ? 's' : ''} found
            </p>

            {loading ? (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-400">Loading providers...</p>
              </div>
            ) : filteredProviders.length === 0 ? (
              <div className="text-center py-12 bg-white/5 border border-white/10 rounded-xl">
                <Search className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <h3 className="text-white font-semibold mb-2">No providers found</h3>
                <p className="text-gray-400">Try adjusting your search or filter criteria.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredProviders.map((provider) => (
                  <div 
                    key={provider.id}
                    className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-blue-500/30 transition"
                    data-testid={`provider-card-${provider.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-start gap-4">
                      {/* Provider Info */}
                      <div className="flex-1">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-6 h-6 text-blue-400" />
                          </div>
                          <div>
                            <h3 className="text-white font-semibold text-lg flex items-center gap-2">
                              {provider.name}
                              {provider.verified && (
                                <CheckCircle className="w-4 h-4 text-green-500" title="Verified Provider" />
                              )}
                            </h3>
                            <p className="text-gray-400 text-sm">
                              {provider.address}, {provider.city}, {provider.state} {provider.zip}
                            </p>
                          </div>
                        </div>

                        {/* Rating */}
                        <div className="flex items-center gap-2 mb-3">
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i} 
                                className={`w-4 h-4 ${i < Math.floor(provider.rating) ? 'text-yellow-400 fill-current' : 'text-gray-600'}`} 
                              />
                            ))}
                          </div>
                          <span className="text-white font-medium">{provider.rating}</span>
                          <span className="text-gray-500 text-sm">({provider.reviews} reviews)</span>
                        </div>

                        {/* Services */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {provider.services.slice(0, 4).map((service, idx) => (
                            <span 
                              key={idx}
                              className="px-2 py-1 bg-blue-500/10 text-blue-400 text-xs rounded-full"
                            >
                              {getServiceLabel(service)}
                            </span>
                          ))}
                        </div>

                        {/* Hours */}
                        <div className="flex items-center gap-2 text-gray-400 text-sm">
                          <Clock className="w-4 h-4" />
                          <span>{provider.hours}</span>
                        </div>
                      </div>

                      {/* Contact Actions */}
                      <div className="flex flex-col gap-2 md:w-48">
                        <a
                          href={`tel:${provider.phone}`}
                          className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition"
                        >
                          <Phone className="w-4 h-4" />
                          Call Now
                        </a>
                        {provider.website && (
                          <a
                            href={provider.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg font-medium transition"
                          >
                            <Globe className="w-4 h-4" />
                            Website
                          </a>
                        )}
                        <a
                          href={`mailto:${provider.email}`}
                          className="flex items-center justify-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg font-medium transition"
                        >
                          <Mail className="w-4 h-4" />
                          Email
                        </a>
                        <a
                          href={`https://maps.google.com/?q=${encodeURIComponent(`${provider.address}, ${provider.city}, ${provider.state} ${provider.zip}`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg font-medium transition"
                        >
                          <Navigation className="w-4 h-4" />
                          Directions
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Become a Provider CTA */}
        <section className="py-12 px-4 bg-gradient-to-r from-purple-900/30 to-blue-900/30 border-t border-white/10">
          <div className="max-w-4xl mx-auto text-center">
            <Building2 className="w-10 h-10 text-purple-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-3">
              Want to List Your Business?
            </h2>
            <p className="text-gray-400 mb-6">
              Join the FingerprintVS network and connect with clients in your area.
            </p>
            <Link 
              to="/for-vendors"
              className="inline-flex items-center gap-2 px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold transition"
            >
              Become a Provider
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 px-4 border-t border-white/10">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Fingerprint className="w-6 h-6 text-blue-400" />
              <span className="text-white font-semibold">FingerprintVS</span>
              <span className="text-gray-500 text-sm">• Fingerprinting Virtual System</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <Link to="/services" className="hover:text-white transition">Services</Link>
              <Link to="/find-provider" className="hover:text-white transition">Find Provider</Link>
              <Link to="/for-vendors" className="hover:text-white transition">For Vendors</Link>
              <a href="mailto:support@fingerprintvs.com" className="hover:text-white transition">Contact</a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default FindProviderPage;
